import { PublicClientApplication, ProtocolMode, type Configuration } from '@azure/msal-browser';
import { fetchRuntimeConfig } from './runtime-config';

// Vite-baked values are the LOCAL-DEV fallback only (EW-242, mirroring the admin
// FE's EW-166 pattern): deployed environments get their auth config at runtime
// from GET /config.json, served by the gateway itself, so the CI image stays
// neutral. initAuth() must complete before anything touches the exports below —
// main.tsx awaits it before the first render.
const envTenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? '';
const envClientId = import.meta.env.VITE_ENTRA_WEB_CLIENT_ID ?? '';
const envAuthorityUrl = import.meta.env.VITE_AUTH_AUTHORITY_URL ?? '';

// EW-227: a missing auth config must surface as a visible error, not a silent
// redirect to login.microsoftonline.com with an empty client_id. initAuth() does
// not throw on it: it records the error and skips the MSAL instantiation, and
// resolveAuth() (auth-gate) turns that into a failed outcome.
export let authConfigError: string | null = null;
export let apiScope = '';
export let msalInstance!: PublicClientApplication;
export let loginRequest: { scopes: string[] } = { scopes: ['openid', 'profile', 'email'] };
export let apiRequest: { scopes: string[] } = { scopes: ['openid'] };

export async function initAuth(): Promise<void> {
  const runtime = (await fetchRuntimeConfig()) ?? {};
  // `||` on purpose: the backend serves all three keys unconditionally, and an
  // empty runtime value must fall back to the VITE_* one.
  const tenantId = runtime.entraTenantId || envTenantId;
  const clientId = runtime.adminClientId || envClientId;
  const authorityUrl = runtime.authorityUrl || envAuthorityUrl;

  // The error texts name the runtime env vars next to the VITE_* ones: on a
  // deployed container the operator sets ECHOPROXY_*, the VITE_* values only
  // exist in local dev. The https check is ours on purpose: MSAL's
  // UrlString.validateAsUri() accepts https only, and our message beats its
  // deep exception.
  authConfigError = !clientId
    ? 'Auth is not configured: no web client id (set ECHOPROXY_AUTH_ADMIN_AUDIENCE on the container, or VITE_ENTRA_WEB_CLIENT_ID in local dev).'
    : !authorityUrl && !tenantId
      ? 'Auth is not configured: no authority URL and no tenant id (set ECHOPROXY_AUTH_AUTHORITY_URL or ECHOPROXY_ENTRA_TENANT_ID on the container; VITE_AUTH_AUTHORITY_URL or VITE_ENTRA_TENANT_ID in local dev).'
      : authorityUrl && !authorityUrl.startsWith('https://')
        ? `Auth is not configured: the authority URL must start with https:// (MSAL accepts nothing else), got "${authorityUrl}" — check ECHOPROXY_AUTH_AUTHORITY_URL (or VITE_AUTH_AUTHORITY_URL in local dev).`
        : null;
  if (authConfigError) return;

  // A generic OIDC issuer gets NO api:// scope — measured on Keycloak, an undeclared
  // scope fails the login with invalid_scope; the audience comes from an IdP-side
  // audience mapper instead.
  apiScope = clientId && !authorityUrl ? `api://${clientId}/access_admin_api` : '';

  // EW-66: generic-OIDC mode when the authority URL is set — MSAL runs against
  // that issuer (hand-written metadata skips the Microsoft cloud-instance
  // discovery a non-Entra issuer would fail).
  const msalConfig: Configuration = {
    auth: {
      clientId,
      redirectUri: window.location.origin + '/auth/callback',
      postLogoutRedirectUri: window.location.origin,
      ...(authorityUrl
        ? {
            authority: authorityUrl,
            knownAuthorities: [new URL(authorityUrl).host],
            protocolMode: ProtocolMode.OIDC,
            authorityMetadata: JSON.stringify({
              issuer: authorityUrl,
              authorization_endpoint: `${authorityUrl}/protocol/openid-connect/auth`,
              token_endpoint: `${authorityUrl}/protocol/openid-connect/token`,
              userinfo_endpoint: `${authorityUrl}/protocol/openid-connect/userinfo`,
              end_session_endpoint: `${authorityUrl}/protocol/openid-connect/logout`,
            }),
          }
        : { authority: `https://login.microsoftonline.com/${tenantId}` }),
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  };

  msalInstance = new PublicClientApplication(msalConfig);
  loginRequest = { scopes: ['openid', 'profile', 'email', ...(apiScope ? [apiScope] : [])] };
  apiRequest = { scopes: apiScope ? [apiScope] : ['openid'] };
}
